// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/Sale.tsx   (روی فایل قبلی بنویسید)
//
// نسخه‌ی جدید:
//  - فروش داخلی / فروش خارجی (آینه‌ی خرید): فروش خارجی بعد از تأیید به «ترانزیت/ترانشیپ» می‌رود
//    و بعد از آن «رسید مشتری» ثبت می‌شود (دقیقاً مسیر معکوس خرید)
//  - در فرم، «جنس» و «گدام» دو فیلد کاملاً جدا هستند (موجودی از گدام کم می‌شود)
//  - لیست: جدول اسکرولی، ۵۰ ردیف در هر صفحه، قبلی/بعدی (صفحه‌بندی سمت سرور)
//  - ضمیمه (فایل / صدا) مثل خرید
//  - گزارش PDF و Excel از همه‌ی رکوردها یا مطابق فیلترها
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, customersAPI, warehouseAPI } from "../../../services/api";
import { usePermission } from "../../../hooks/usePermission";
import { useSubmit } from "../../../hooks/useSubmit";
import SearchableSelect from "../../../components/SearchableSelect";
import AttachmentField from "../../../components/AttachmentField";
import PersianDatePicker, { todayGregorian, DualDate } from "../../../utils/PersianDatePicker";
import { Plus, Trash2, Eye, Printer, Edit2, Ship, Truck, Paperclip } from "lucide-react";
import { printSaleInvoice } from "../../../utils/InvoicePrint";
import { fileUrl } from "../../../utils/txnReport";
import {
  inputSt, rowSt, badgeSt, addBtnSt, iconBtnSt,
  Lbl, BtnCancel, BtnSave, Toast, Loading, Empty, Modal,
} from "./Purchase";
import JournalFilter, { emptyFilter, JournalFilterValues } from "./JournalFilter";
import {
  StatusTabs, PendingBadge, ConfirmIconButton, RevertIconButton,
  PendingSubmitButton, ConfirmSubmitButton, PendingHint, pendingRowStyle,
} from "./PendingControls";
import { useTxnList, describeFilter } from "./useTxnList";
import TxnReportModal from "./TxnReportModal";

const fmt = (n: any) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

const calcTotal = (qty: any, price: any) => (parseFloat(String(qty)) || 0) * (parseFloat(String(price)) || 0);

interface Item {
  product_id: string;      // "12" برای جنس یا "svc_5" برای خدمت
  product_name: string;
  unit: string;
  quantity: string;
  warehouse_id: string;    // گدام (جدا از جنس)
  warehouse_name: string;
  unit_price: string;
  total_price: number;
}

interface ProductOption { value: string; label: string; product_id: number; product_name: string; unit: string; item_type: "product" | "service"; }

const emptyItem = (): Item => ({ product_id: "", product_name: "", unit: "", quantity: "1", warehouse_id: "", warehouse_name: "", unit_price: "0", total_price: 0 });

const emptyForm = () => ({
  id: 0, status: "confirmed" as string,
  date: todayGregorian(), customer_id: "", customer_currency: "USD",
  discount: "0", notes: "", attachment: "", attachment_file_name: "",
  truck_plate: "", waybill_number: "", sale_type: "domestic" as "domestic" | "foreign",
});

export default function Sale() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("finance", "add");
  const canEdit   = usePermission("finance", "edit");
  const canDelete = usePermission("finance", "delete");
  const { submitting, submit } = useSubmit();

  const [showForm, setShowForm]   = useState(false);
  const [viewItem, setViewItem]   = useState<any>(null);
  const [showReport, setShowReport] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [filter, setFilter]       = useState<JournalFilterValues>(emptyFilter);
  const [toast, setToast]         = useState({ text: "", ok: true });
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "confirmed">("all");
  const [typeFilter, setTypeFilter] = useState<"" | "domestic" | "foreign">("");

  const [form, setForm]   = useState(emptyForm());
  const [items, setItems] = useState<Item[]>([]);
  const isEdit = form.id > 0;
  const isForeign = form.sale_type === "foreign";

  const { list, loading, page, setPage, totalPages, totalAll, pendingCount, confirmedCount, reload } = useTxnList({
    fetcher: (p) => journalAPI.listSales(p),
    respKey: "sales",
    filter, statusFilter,
    extra: { sale_type: typeFilter || undefined },
  });

  const loadMeta = useCallback(async () => {
    const [c, pr, w, u, sv] = await Promise.all([
      customersAPI.list(),
      warehouseAPI.productsList(),
      warehouseAPI.warehousesList(),
      warehouseAPI.unitsList(),
      warehouseAPI.servicesList(),
    ]);
    if (c.success) setCustomers(c.customers || []);
    if (w.success) setWarehouses(w.warehouses || []);
    const opts: ProductOption[] = [];
    if (pr.success) {
      const units = u.success ? (u.units || []) : [];
      (pr.products || []).forEach((prod: any) => {
        const unit = units.find((x: any) => x.id === prod.unit_id)?.name || "";
        opts.push({ value: String(prod.id), label: `📦 ${prod.name}`, product_id: prod.id, product_name: prod.name, unit, item_type: "product" });
      });
    }
    if (sv.success) {
      (sv.services || []).forEach((svc: any) => {
        opts.push({ value: `svc_${svc.id}`, label: `🔧 ${svc.name}`, product_id: svc.id, product_name: svc.name, unit: "", item_type: "service" });
      });
    }
    setProductOptions(opts);
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const custName = (c: any) => `${c?.first_name || ""} ${c?.last_name || ""} ${c?.company_name || ""}`.trim();
  const counterpartyOptions = customers.map((c: any) => ({ value: String(c.id), label: custName(c) }));

  // ───────── آیتم‌ها ─────────
  const addItem = () => setItems(p => [...p, emptyItem()]);
  const removeItem = (i: number) => setItems(p => p.filter((_, idx) => idx !== i));
  const setItemProduct = (i: number, val: string) => {
    const opt = productOptions.find(o => o.value === val);
    setItems(p => p.map((it, idx) => {
      if (idx !== i) return it;
      const u = { ...it, product_id: val, product_name: opt?.product_name || "", unit: opt?.unit || "" };
      if (opt?.item_type === "service") { u.warehouse_id = ""; u.warehouse_name = ""; }
      return u;
    }));
  };
  const setItemWarehouse = (i: number, val: string) => {
    const wh = warehouses.find((w: any) => String(w.id) === val);
    setItems(p => p.map((it, idx) => idx === i ? { ...it, warehouse_id: val, warehouse_name: wh?.name || "" } : it));
  };
  const setItemNum = (i: number, key: "quantity" | "unit_price", val: string) => {
    setItems(p => p.map((it, idx) => {
      if (idx !== i) return it;
      const u = { ...it, [key]: val };
      u.total_price = calcTotal(u.quantity, u.unit_price);
      return u;
    }));
  };

  const totalAmount = items.reduce((s, i) => s + i.total_price, 0);
  const netAmount = totalAmount - (parseFloat(form.discount || "0") || 0);

  // ───────── ثبت و ویرایش ─────────
  const buildPayload = (statusValue: "pending" | "confirmed") => {
    const customer = customers.find((c: any) => String(c.id) === form.customer_id);
    return {
      date: form.date,
      customer_id: parseInt(form.customer_id),
      customer_name: custName(customer),
      customer_currency: customer?.currency || form.customer_currency,
      discount: parseFloat(form.discount || "0"),
      sale_type: form.sale_type,
      items: items.map(i => {
        const isService = i.product_id.startsWith("svc_");
        return {
          product_id: isService ? parseInt(i.product_id.replace("svc_", "")) : (i.product_id ? parseInt(i.product_id) : null),
          product_name: i.product_name,
          unit: i.unit,
          quantity: parseFloat(String(i.quantity)) || 0,
          warehouse_id: (!isService && i.warehouse_id) ? parseInt(i.warehouse_id) : null,
          warehouse_name: isService ? "" : i.warehouse_name,
          unit_price: parseFloat(String(i.unit_price)) || 0,
          total_price: i.total_price,
          item_type: isService ? "service" : "product",
        };
      }),
      notes: form.notes,
      attachment: form.attachment,
      truck_plate: form.truck_plate,
      waybill_number: form.waybill_number,
      status: statusValue,
    };
  };

  const validateForm = () => {
    if (!form.customer_id) { showToast("مشتری را انتخاب کنید", false); return false; }
    if (items.length === 0) { showToast("حداقل یک جنس اضافه کنید", false); return false; }
    for (const i of items) {
      if (!i.product_name) { showToast("برای همه‌ی ردیف‌ها «جنس» را انتخاب کنید", false); return false; }
      if ((parseFloat(String(i.quantity)) || 0) <= 0) { showToast("تعداد باید بیشتر از صفر باشد", false); return false; }
      if (!i.product_id.startsWith("svc_") && !i.warehouse_id) { showToast(`برای «${i.product_name}» گدام را انتخاب کنید`, false); return false; }
    }
    return true;
  };

  const resetForm = () => { setForm(emptyForm()); setItems([]); };
  const openCreate = () => { setForm(emptyForm()); setItems([emptyItem()]); setShowForm(true); };

  const openEdit = (s: any) => {
    setForm({
      id: s.id, status: s.status || "confirmed", date: s.date,
      customer_id: String(s.customer_id), customer_currency: s.customer_currency,
      discount: String(s.discount || "0"), notes: s.notes || "",
      attachment: s.attachment || "", attachment_file_name: s.attachment ? String(s.attachment).split("/").pop() || "" : "",
      truck_plate: s.truck_plate || "", waybill_number: s.waybill_number || "",
      sale_type: s.sale_type === "foreign" ? "foreign" : "domestic",
    });
    setItems((s.items || []).map((it: any) => {
      const isService = !it.warehouse_id;
      return {
        product_id: isService ? `svc_${it.product_id}` : String(it.product_id),
        product_name: it.product_name, unit: it.unit || "",
        quantity: String(parseFloat(it.quantity)), warehouse_id: it.warehouse_id ? String(it.warehouse_id) : "",
        warehouse_name: it.warehouse_name || "", unit_price: String(parseFloat(it.unit_price)),
        total_price: Number(it.total_price),
      } as Item;
    }));
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); resetForm(); };

  const handleCreate = async (statusValue: "pending" | "confirmed") => {
    if (!validateForm()) return;
    const res = await journalAPI.addSale(buildPayload(statusValue));
    if (res.success) {
      showToast(statusValue === "pending" ? `فروش ${res.serial} در انتظار تأیید ذخیره شد` : `فروش ${res.serial} با موفقیت ثبت شد`);
      closeForm(); reload();
    } else showToast(res.message || "خطا در ثبت فروش", false);
  };

  const handleEdit = async () => {
    if (!validateForm()) return;
    const p: any = buildPayload(form.status === "pending" ? "pending" : "confirmed");
    delete p.status; delete p.sale_type;
    const res = await journalAPI.updateSale({ ...p, id: form.id });
    if (res.success) { showToast("فروش ویرایش شد"); closeForm(); reload(); }
    else showToast(res.message || "خطا", false);
  };

  const handleConfirm = async (id: number, serial: string) => {
    if (!window.confirm(`تأیید نهایی فروش ${serial}؟\nموجودی گدام، حساب درآمد و بیلانس مشتری به‌روزرسانی می‌شود.`)) return;
    const res = await journalAPI.confirmSale(id);
    if (res.success) { showToast("فروش تأیید شد"); reload(); } else showToast(res.message || "خطا", false);
  };
  const handleRevert = async (id: number, serial: string) => {
    if (!window.confirm(`برگرداندن فروش ${serial} به حالت در انتظار؟\nتأثیرات آن لغو می‌شود.`)) return;
    const res = await journalAPI.revertSale(id);
    if (res.success) { showToast("به حالت در انتظار برگشت"); reload(); } else showToast(res.message || "خطا", false);
  };
  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف فروش ${serial}؟`)) return;
    const res = await journalAPI.deleteSale(id);
    if (res.success) { showToast("فروش حذف شد"); reload(); } else showToast(res.message || "خطا", false);
  };

  const filtersLabel = describeFilter(filter, { counterparty: counterpartyOptions, counterpartyLabel: "مشتری", extra: [typeFilter ? `نوع: ${typeFilter === "foreign" ? "فروش خارجی" : "فروش داخلی"}` : ""] });

  const typeChip = (v: "" | "domestic" | "foreign", label: string, c: string) => (
    <button key={v} onClick={() => setTypeFilter(v)} style={{
      padding: "7px 16px", borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
      border: `1.5px solid ${typeFilter === v ? c : T.cardBorder}`, background: typeFilter === v ? c + "18" : "transparent", color: typeFilter === v ? c : T.subtext,
    }}>{label}</button>
  );

  const COLS = "125px 105px 150px 1fr 105px 70px 1.1fr 165px 210px";

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && <Toast text={toast.text} ok={toast.ok} />}

      <JournalFilter
        config={{
          searchPlaceholder: "جستجو در فروشات... (شماره سند، نام مشتری، موتر، توضیحات)",
          showDateRange: true, showAmountRange: true, showCurrency: true,
          showCounterparty: true, counterpartyLabel: "مشتری", counterpartyOptions,
          showProductName: true,
        }}
        values={filter}
        onChange={setFilter}
        resultCount={list.length}
        totalCount={totalAll}
        page={page} totalPages={totalPages} onPageChange={setPage}
        T={T} dark={dark}
        onReport={() => setShowReport(true)}
        reportLabel="گزارش PDF / Excel فروشات"
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <StatusTabs value={statusFilter} onChange={setStatusFilter} totalCount={pendingCount + confirmedCount} pendingCount={pendingCount} T={T} dark={dark} />
          <div style={{ display: "flex", gap: 6 }}>
            {typeChip("", "همه", "#64748b")}
            {typeChip("domestic", "فروش داخلی", "#10b981")}
            {typeChip("foreign", "فروش خارجی", "#8b5cf6")}
          </div>
        </div>
        {canAdd && (
          <button onClick={openCreate} style={addBtnSt("#10b981")}><Plus size={15} /> ثبت فروش جدید</button>
        )}
      </div>

      {loading ? <Loading T={T} /> : (
        <div style={{ maxHeight: "68vh", overflowY: "auto", overflowX: "auto", paddingLeft: 2, paddingBottom: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 1240 }}>
            {list.length === 0 && <Empty T={T} text="هیچ فروشی یافت نشد" />}
            {list.length > 0 && (
              <div style={{
                display: "grid", gridTemplateColumns: COLS, gap: 12, padding: "10px 16px", borderRadius: 10,
                position: "sticky", top: 0, zIndex: 2,
                background: dark ? "#1e293b" : "#f1f5f9", border: `1px solid ${T.cardBorder}`, fontSize: 11, fontWeight: 700, color: T.subtext,
              }}>
                <div>شماره سند</div><div>نوع</div><div>موتر / بارنامه</div><div>مشتری</div><div>تاریخ</div>
                <div style={{ textAlign: "center" }}>آیتم‌ها</div><div>شرح</div><div style={{ textAlign: "left" }}>مبلغ خالص</div><div style={{ textAlign: "center" }}>عملیات</div>
              </div>
            )}
            {list.map((s: any) => {
              const isPending = (s.status || "confirmed") === "pending";
              const foreign = s.sale_type === "foreign";
              return (
                <div key={s.id} style={{ ...rowSt(dark, T), ...(isPending ? pendingRowStyle(dark) : {}), display: "grid", gridTemplateColumns: COLS, gap: 12, alignItems: "center" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    <span style={badgeSt("#10b981")}>{s.serial}</span>
                    {isPending && <PendingBadge />}
                    {s.attachment && <a href={fileUrl(s.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 10.5, color: "#3b82f6", display: "inline-flex", alignItems: "center", gap: 3, textDecoration: "none" }}><Paperclip size={11} /> ضمیمه</a>}
                  </div>
                  <div>
                    {foreign
                      ? <span style={{ ...badgeSt("#8b5cf6"), display: "inline-flex", alignItems: "center", gap: 4 }}><Ship size={11} /> خارجی</span>
                      : <span style={{ ...badgeSt("#10b981"), display: "inline-flex", alignItems: "center", gap: 4 }}><Truck size={11} /> داخلی</span>}
                    {foreign && s.transit_status === "pending" && <div style={{ fontSize: 10, color: "#8b5cf6", marginTop: 3 }}>در انتظار ترانزیت</div>}
                    {foreign && s.transit_status === "in_transit" && <div style={{ fontSize: 10, color: "#f59e0b", marginTop: 3, fontWeight: 700 }}>🚛 در مسیر (ترانزیت)</div>}
                    {foreign && s.transit_status === "completed" && <div style={{ fontSize: 10, color: "#10b981", marginTop: 3, fontWeight: 700 }}>✓ رسید مشتری تکمیل شد</div>}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                    {s.truck_plate && <span style={{ padding: "3px 10px", borderRadius: 7, background: "rgba(59,130,246,0.1)", color: "#3b82f6", fontSize: 11, fontWeight: 700, fontFamily: "monospace", whiteSpace: "nowrap" }}>🚚 {s.truck_plate}</span>}
                    {s.waybill_number && <span style={{ padding: "3px 10px", borderRadius: 7, background: "rgba(14,165,233,0.1)", color: "#0ea5e9", fontSize: 11, fontWeight: 700, fontFamily: "monospace", whiteSpace: "nowrap" }}>📄 {s.waybill_number}</span>}
                  </div>
                  <div style={{ fontWeight: 700, color: T.text, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis" }}>{s.customer_name}</div>
                  <div><DualDate date={s.date} fontSize={12} color={T.subtext} /></div>
                  <div style={{ fontSize: 12, color: T.subtext, textAlign: "center" }}>{s.items?.length || 0} آیتم</div>
                  <div style={{ fontSize: 11, color: T.subtext, fontStyle: "italic", lineHeight: 1.5, wordBreak: "break-word" }}>{s.notes || "—"}</div>
                  <div style={{ fontWeight: 800, color: "#10b981", fontSize: 14, textAlign: "left", direction: "ltr" }}>{fmt(s.net_amount)} {s.customer_currency}</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
                    <button onClick={() => setViewItem(s)} title="مشاهده" style={iconBtnSt("#8b5cf6")}><Eye size={13} /></button>
                    <button onClick={() => { const cust = customers.find((c: any) => c.id === s.customer_id); printSaleInvoice(s, cust, cust?.balance || 0); }} title="چاپ فاکتور" style={iconBtnSt("#0ea5e9")}><Printer size={13} /></button>
                    {isPending && canEdit && <ConfirmIconButton onClick={() => handleConfirm(s.id, s.serial)} />}
                    {!isPending && canEdit && <RevertIconButton onClick={() => handleRevert(s.id, s.serial)} />}
                    {canEdit && <button onClick={() => openEdit(s)} title="ویرایش" style={iconBtnSt("#3b82f6")}><Edit2 size={13} /></button>}
                    {canDelete && <button onClick={() => handleDelete(s.id, s.serial)} title="حذف" style={iconBtnSt("#ef4444")}><Trash2 size={13} /></button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ───────── فرم ثبت / ویرایش فروش ───────── */}
      {showForm && (
        <Modal title={isEdit ? `ویرایش فروش${form.status === "pending" ? " (در انتظار)" : ""}` : "ثبت فروش جدید"} onClose={closeForm} dark={dark} T={T}>
          {/* نوع فروش */}
          <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
            {(["domestic", "foreign"] as const).map(t => {
              const active = form.sale_type === t;
              const c = t === "foreign" ? "#8b5cf6" : "#10b981";
              return (
                <button key={t} disabled={isEdit} onClick={() => setForm(f => ({ ...f, sale_type: t }))} style={{
                  flex: 1, padding: "12px 16px", borderRadius: 12, cursor: isEdit ? "default" : "pointer",
                  border: `2px solid ${active ? c : T.cardBorder}`, background: active ? c + "1a" : "transparent", color: active ? c : T.subtext,
                  fontWeight: 800, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: isEdit && !active ? 0.4 : 1,
                  fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
                }}>
                  {t === "foreign" ? <Ship size={16} /> : <Truck size={16} />}
                  {t === "foreign" ? "فروش خارجی" : "فروش داخلی"}
                </button>
              );
            })}
          </div>
          {isForeign && (
            <div style={{ background: dark ? "rgba(139,92,246,0.08)" : "#f5f3ff", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#8b5cf6", lineHeight: 1.8 }}>
              ℹ️ فروش خارجی: موجودی از گدامِ انتخاب‌شده کم می‌شود و جنس به بخش «ترانزیت/ترانشیپ» می‌رود. بعد از رسیدن بار، «رسید مشتری» ثبت می‌شود.
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div>
              <Lbl T={T}>مشتری *</Lbl>
              <SearchableSelect
                options={customers.map((c: any) => ({ value: String(c.id), label: custName(c) + ` (${c.currency})` }))}
                value={form.customer_id}
                onChange={v => { const cu = customers.find((x: any) => String(x.id) === v); setForm(f => ({ ...f, customer_id: v, customer_currency: cu?.currency || "USD" })); }}
                placeholder="انتخاب مشتری"
              />
            </div>
            <div><Lbl T={T}>تاریخ</Lbl><PersianDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} /></div>
            <div><Lbl T={T}>تخفیف</Lbl><input value={form.discount} onChange={e => setForm(f => ({ ...f, discount: e.target.value }))} placeholder="0" style={{ ...inputSt(T), direction: "ltr" }} /></div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <strong style={{ fontSize: 13, color: T.text }}>اجناس فروش</strong>
              <button onClick={addItem} style={addBtnSt("#10b981", true)}><Plus size={13} /> افزودن جنس</button>
            </div>
            {items.map((item, i) => {
              const isService = item.product_id.startsWith("svc_");
              return (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1.6fr 80px 90px 100px 110px 36px", gap: 8, marginBottom: 10, alignItems: "end", padding: 12, borderRadius: 10, background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc", border: `1px solid ${T.border}` }}>
                  <div>
                    {i === 0 && <Lbl T={T}>جنس / خدمت *</Lbl>}
                    <SearchableSelect options={productOptions.map(o => ({ value: o.value, label: o.label }))} value={item.product_id} onChange={v => setItemProduct(i, v)} placeholder="انتخاب جنس" />
                  </div>
                  <div>
                    {i === 0 && <Lbl T={T}>گدام *</Lbl>}
                    {isService
                      ? <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.subtext, fontSize: 12.5, minHeight: 42 }}>خدمت — بدون گدام</div>
                      : <SearchableSelect options={warehouses.map((w: any) => ({ value: String(w.id), label: `🏭 ${w.name}` }))} value={item.warehouse_id} onChange={v => setItemWarehouse(i, v)} placeholder="انتخاب گدام" />}
                  </div>
                  <div>
                    {i === 0 && <Lbl T={T}>واحد</Lbl>}
                    <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1.5px solid ${T.border}`, color: T.text, fontSize: 13, minHeight: 42 }}>{item.unit || "—"}</div>
                  </div>
                  <div>
                    {i === 0 && <Lbl T={T}>تعداد</Lbl>}
                    <input type="text" inputMode="decimal" value={item.quantity} onChange={e => setItemNum(i, "quantity", e.target.value)} placeholder="0.0" style={{ ...inputSt(T), direction: "ltr", textAlign: "right" }} />
                  </div>
                  <div>
                    {i === 0 && <Lbl T={T}>قیمت واحد</Lbl>}
                    <input type="text" inputMode="decimal" value={item.unit_price} onChange={e => setItemNum(i, "unit_price", e.target.value)} placeholder="0" style={{ ...inputSt(T), direction: "ltr", textAlign: "right" }} />
                  </div>
                  <div>
                    {i === 0 && <Lbl T={T}>مجموع</Lbl>}
                    <div style={{ padding: "10px 12px", borderRadius: 10, background: dark ? "rgba(16,185,129,0.1)" : "#f0fdf4", color: "#10b981", fontWeight: 700, fontSize: 13, direction: "ltr", minHeight: 42 }}>{fmt(item.total_price)}</div>
                  </div>
                  <div style={{ marginTop: i === 0 ? 22 : 0 }}>
                    <button onClick={() => removeItem(i)} style={{ ...iconBtnSt("#ef4444"), width: 36, height: 36 }}><Trash2 size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ background: dark ? "rgba(16,185,129,0.08)" : "#f0fdf4", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 28, flexWrap: "wrap" }}>
            <div><span style={{ fontSize: 12, color: T.subtext }}>جمع کل: </span><strong style={{ color: T.text }}>{fmt(totalAmount)}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>تخفیف: </span><strong style={{ color: "#ef4444" }}>{fmt(parseFloat(form.discount || "0"))}</strong></div>
            <div><span style={{ fontSize: 12, color: T.subtext }}>خالص: </span><strong style={{ color: "#10b981", fontSize: 16 }}>{fmt(netAmount)} {form.customer_currency}</strong></div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <div><Lbl T={T}>🚚 شماره موتر</Lbl><input value={form.truck_plate} onChange={e => setForm(f => ({ ...f, truck_plate: e.target.value }))} placeholder="مثلاً: 12345" style={{ ...inputSt(T), direction: "ltr" }} /></div>
            <div><Lbl T={T}>📄 شماره بارنامه</Lbl><input value={form.waybill_number} onChange={e => setForm(f => ({ ...f, waybill_number: e.target.value }))} placeholder="شماره بارنامه..." style={{ ...inputSt(T), direction: "ltr" }} /></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <div><Lbl T={T}>توضیحات</Lbl><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="توضیحات..." style={inputSt(T)} /></div>
            <div>
              <Lbl T={T}>ضمیمه (فایل یا صدا)</Lbl>
              <AttachmentField
                value={form.attachment} fileName={form.attachment_file_name}
                onChange={(path, name) => setForm(f => ({ ...f, attachment: path, attachment_file_name: name }))}
                uploadFn={(file) => journalAPI.uploadAttachment(file)} T={T} dark={dark}
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "center", marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.cardBorder}`, flexWrap: "wrap" }}>
            {isEdit ? <span /> : <PendingHint T={T} />}
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <BtnCancel onClick={closeForm} T={T} />
              {isEdit ? (
                <BtnSave onClick={() => submit(handleEdit)} loading={submitting} color="#3b82f6" label="ذخیره ویرایش" />
              ) : (
                <>
                  <PendingSubmitButton onClick={() => submit(() => handleCreate("pending"))} loading={submitting} label="ذخیره فروش در انتظار" />
                  <ConfirmSubmitButton onClick={() => submit(() => handleCreate("confirmed"))} loading={submitting} color="#10b981" label="ثبت فروش" />
                </>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* ───────── جزئیات ───────── */}
      {viewItem && (
        <Modal title={`جزئیات فروش: ${viewItem.serial}`} onClose={() => setViewItem(null)} dark={dark} T={T}>
          <div id="print-sale">
            <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: T.subtext }}>نوع: <strong style={{ color: viewItem.sale_type === "foreign" ? "#8b5cf6" : "#10b981" }}>{viewItem.sale_type === "foreign" ? "فروش خارجی" : "فروش داخلی"}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext }}>مشتری: <strong style={{ color: T.text }}>{viewItem.customer_name}</strong></span>
              <span style={{ fontSize: 12, color: T.subtext, display: "inline-flex", alignItems: "center", gap: 6 }}>تاریخ: <DualDate date={viewItem.date} fontSize={12} color={T.text} inline /></span>
              <span style={{ fontSize: 12, color: T.subtext }}>خالص: <strong style={{ color: "#10b981" }}>{fmt(viewItem.net_amount)} {viewItem.customer_currency}</strong></span>
              {viewItem.truck_plate && <span style={{ fontSize: 12, color: T.subtext }}>🚚 شماره موتر: <strong style={{ color: "#3b82f6" }}>{viewItem.truck_plate}</strong></span>}
              {viewItem.waybill_number && <span style={{ fontSize: 12, color: T.subtext }}>📄 شماره بارنامه: <strong style={{ color: "#0ea5e9" }}>{viewItem.waybill_number}</strong></span>}
              {viewItem.notes && <span style={{ fontSize: 12, color: T.subtext }}>توضیح: <strong style={{ color: T.text }}>{viewItem.notes}</strong></span>}
              {viewItem.attachment && <a href={fileUrl(viewItem.attachment)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#3b82f6", fontWeight: 700 }}>📎 مشاهده‌ی ضمیمه</a>}
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: dark ? "rgba(255,255,255,0.05)" : "#f8fafc" }}>
                  {["جنس", "کود", "گدام", "واحد", "تعداد", "قیمت واحد", "مجموع"].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: T.subtext, borderBottom: `1px solid ${T.cardBorder}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {viewItem.items?.map((it: any, idx: number) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${T.cardBorder}` }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.product_name}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.product_code || "—"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.warehouse_name || "—"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{it.unit}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{parseFloat(it.quantity)}{it.base_unit_name && it.base_quantity != null ? ` (= ${parseFloat(it.base_quantity)} ${it.base_unit_name})` : ""}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: T.text }}>{fmt(it.unit_price)}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#10b981" }}>{fmt(it.total_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button onClick={() => { const cust = customers.find((c: any) => c.id === viewItem.customer_id); printSaleInvoice(viewItem, cust, cust?.balance || 0); }} style={addBtnSt("#8b5cf6")}>
              <Printer size={14} /> چاپ فاکتور
            </button>
          </div>
        </Modal>
      )}

      {showReport && (
        <TxnReportModal type="sales" title="فروشات" filter={filter} filtersLabel={filtersLabel}
          extraParams={{ sale_type: typeFilter || undefined }} onClose={() => setShowReport(false)} T={T} dark={dark} />
      )}
    </div>
  );
}
